/**
 * Dashboard Page for Comments Press Zone
 */
import { el, mount, clear, __, showLoading } from '../utils/dom.js';
import API from '../utils/api.js';
import StatCard from '../components/StatCard.js';
import { Card } from '../components/Card.js';
import { EmptyState } from '../components/EmptyState.js';

export async function renderDashboard(container) {
    showLoading(container, __('Loading dashboard...', 'comments-press-zone'));

    try {
        // Fetch all data in parallel
        const [stats, activity, topCommenters, reportsData, modActivity] = await Promise.all([
            API.get('/dashboard/stats'),
            API.get('/dashboard/activity'),
            API.get('/dashboard/top-commenters?period=30'),
            API.get('/reports'),
            API.get('/dashboard/mod-activity?limit=8')
        ]);

        const reports = reportsData.reports || [];

        clear(container);

        // Header
        const header = el('header', { class: 'presszone-comments-settings__header' },
            el('h1', {}, __('Dashboard', 'comments-press-zone'))
        );

        // Stats Grid (4 cards)
        const statsGrid = el('div', { class: 'presszone-comments-dashboard__stats-grid' },
            StatCard({
                label: __('Total Comments', 'comments-press-zone'),
                value: stats.total_comments,
                icon: '💬',
                growth: stats.growth.comments,
                variant: 'primary',
                comparison: __('vs last 30 days', 'comments-press-zone')
            }),
            StatCard({
                label: __('Total Replies', 'comments-press-zone'),
                value: stats.total_replies,
                icon: '💭',
                growth: stats.growth.replies,
                comparison: __('vs last 30 days', 'comments-press-zone')
            }),
            StatCard({
                label: __('Articles with Comments', 'comments-press-zone'),
                value: stats.posts_with_comments,
                icon: '📄',
                growth: stats.growth.posts,
                comparison: __('vs last 30 days', 'comments-press-zone')
            }),
            StatCard({
                label: __('Avg Comments per Article', 'comments-press-zone'),
                value: stats.avg_comments_per_post.toFixed(1),
                icon: '⭐',
                growth: stats.growth.avg,
                comparison: __('vs last 30 days', 'comments-press-zone')
            })
        );

        // Activity Chart
        const activityChart = createActivityChart(activity);

        // Bottom 3-column grid
        const bottomGrid = el('div', { class: 'presszone-comments-dashboard__bottom-grid' },
            createTopCommentersCard(topCommenters || []),
            createReportsCard(reports || []),
            createModActivityCard(modActivity || [])
        );

        mount(container, header);
        mount(container, statsGrid);
        mount(container, activityChart);
        mount(container, bottomGrid);

    } catch (error) {
        clear(container);
        mount(container, el('div', { class: 'presszone-comments-error' }, error.message));
    }
}

/**
 * Create activity chart component (SVG Area Chart)
 */
function createActivityChart(data) {
    if (!data || !data.last_14_days) {
        return Card('📈', __('Activity (Last 14 Days)', 'comments-press-zone'), '', [
            el('p', { class: 'presszone-comments-empty' }, __('No activity data available', 'comments-press-zone'))
        ]);
    }

    const chartData = data.last_14_days;
    const maxCount = Math.max(...chartData.map(d => d.count), 5); // Ensure at least 5 for scale

    // SVG Dimensions
    const width = 1000;
    const height = 300;
    const padding = 20;

    // Generate Path
    const points = chartData.map((day, index) => {
        const x = (index / (chartData.length - 1)) * width;
        const y = height - ((day.count / maxCount) * (height - padding * 2)) - padding;
        return [x, y];
    });

    const pathD = points.map((p, i) => (i === 0 ? 'M' : 'L') + `${p[0]},${p[1]}`).join(' ');
    const areaD = `${pathD} L ${width},${height} L 0,${height} Z`;

    const chartContainer = el('div', { class: 'presszone-comments-chart-container' });

    // Generate SVG HTML
    chartContainer.innerHTML = `
        <svg viewBox="0 0 ${width} ${height}" preserveAspectRatio="none" class="presszone-comments-chart-svg">
            <defs>
                <linearGradient id="chartFill" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" class="presszone-comments-chart-gradient-start"/>
                    <stop offset="100%" class="presszone-comments-chart-gradient-end"/>
                </linearGradient>
            </defs>
            <path d="${areaD}" fill="url(#chartFill)" />
            <path d="${pathD}" fill="none" class="presszone-comments-chart-line"/>
            ${points.map((p, i) => `
                <circle cx="${p[0]}" cy="${p[1]}" r="4" class="presszone-comments-chart-point" data-index="${i}"/>
            `).join('')}
            ${chartData.map((_, i) => `
                <rect x="${(i / chartData.length) * width}" y="0" width="${width / chartData.length}" height="${height}" fill="transparent" class="presszone-comments-chart-hover-target" data-index="${i}"/>
            `).join('')}
        </svg>
        <div class="presszone-comments-chart-tooltip"></div>
    `;

    // Add interactions
    const svg = chartContainer.querySelector('svg');
    const tooltip = chartContainer.querySelector('.presszone-comments-chart-tooltip');
    const pointElements = chartContainer.querySelectorAll('.presszone-comments-chart-point');

    chartContainer.querySelectorAll('.presszone-comments-chart-hover-target').forEach(rect => {
        rect.addEventListener('mouseenter', (e) => {
            const index = parseInt(e.target.dataset.index);
            const day = chartData[index];
            const point = pointElements[index];

            // Highlight point
            if (point) point.classList.add('is-visible');

            // Show tooltip
            tooltip.classList.add('is-visible');
            tooltip.innerHTML = `
                <div class="tooltip-date">${new Date(day.date).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}</div>
                <div class="tooltip-value">${day.count} comments</div>
                <div class="tooltip-meta">${day.new} new • ${day.replies} replies</div>
            `;

            // Position tooltip
            const rectBox = e.target.getBoundingClientRect();
            const containerBox = chartContainer.getBoundingClientRect();
            let left = rectBox.left - containerBox.left + (rectBox.width / 2) - (tooltip.offsetWidth / 2);

            // Clamp inside container
            left = Math.max(10, Math.min(left, containerBox.width - tooltip.offsetWidth - 10));

            tooltip.style.transform = `translate(${left}px, ${points[index][1] - 60}px)`; // Position above point
        });

        rect.addEventListener('mouseleave', (e) => {
            const index = parseInt(e.target.dataset.index);
            const point = pointElements[index];
            if (point) point.classList.remove('is-visible');
            tooltip.classList.remove('is-visible');
        });
    });

    const description = `${__('Daily comment activity', 'comments-press-zone')} • ${__('Avg:', 'comments-press-zone')} ${data.avg_daily}`;

    const card = Card('📈', __('Activity (Last 14 Days)', 'comments-press-zone'), description, [chartContainer]);
    card.classList.add('presszone-comments-card--overflow-visible');
    return card;
}

/**
 * Create top commenterscard
 */
function createTopCommentersCard(commenters) {
    if (!commenters || commenters.length === 0) {
        return Card('🏆', __('Top Commenters (30 Days)', 'comments-press-zone'),
            __('Most active commenters', 'comments-press-zone'), [
            EmptyState(
                '👥',
                __('Comments Await', 'comments-press-zone'),
                __('Your future top contributors are just around the corner!', 'comments-press-zone'),
                null,
                'welcome'
            )
        ]);
    }

    const medals = ['🥇', '🥈', '🥉'];

    const items = commenters.slice(0, 5).map((user, index) => {
        const rank = medals[index] || `#${index + 1}`;

        return el('div', { class: 'presszone-comments-leaderboard__item' },
            el('span', { class: 'presszone-comments-leaderboard__rank' }, rank),
            el('img', {
                src: user.avatar_url,
                class: 'presszone-comments-leaderboard__avatar',
                alt: user.display_name
            }),
            el('span', { class: 'presszone-comments-leaderboard__name' }, user.display_name),
            el('span', { class: 'presszone-comments-leaderboard__count' }, `${user.comment_count} 💬`)
        );
    });

    const list = el('div', { class: 'presszone-comments-leaderboard' }, ...items);

    return Card('🏆', __('Top Commenters (30 Days)', 'comments-press-zone'),
        __('Most active commenters', 'comments-press-zone'), [list]);
}

/**
 * Create reports card
 */
function createReportsCard(reports) {
    if (!reports || reports.length === 0) {
        return Card('🚩', __('Recent Reports', 'comments-press-zone'),
            __('Latest items requiring attention', 'comments-press-zone'), [
            el('div', { class: 'presszone-comments-empty-state' },
                el('div', { class: 'presszone-comments-empty-state__icon' }, '✅'),
                el('h3', {}, __('All clear!', 'comments-press-zone')),
                el('p', {}, __('No reports need your attention right now.', 'comments-press-zone'))
            )
        ]);
    }

    const items = reports.slice(0, 5).map(report => {
        return el('div', { class: 'presszone-comments-list-item' },
            el('div', { class: 'presszone-comments-list-item__content' },
                el('strong', {}, report.reporter_name || __('Anonymous', 'comments-press-zone')),
                el('p', {}, report.content_preview || __('No content', 'comments-press-zone'))
            ),
            el('a', {
                href: `#/moderation/reports`,
                class: 'presszone-comments-btn presszone-comments-btn--sm'
            }, __('View', 'comments-press-zone'))
        );
    });

    const list = el('div', { class: 'presszone-comments-reports-list' }, ...items);

    return Card('🚩', __('Recent Reports', 'comments-press-zone'),
        __('Latest items requiring attention', 'comments-press-zone'), [list]);
}

/**
 * Create moderation activity card
 */
function createModActivityCard(activities) {
    if (!activities || activities.length === 0) {
        return Card('📋', __('Recent Moderation Activity', 'comments-press-zone'),
            __('Actions taken by moderators', 'comments-press-zone'), [
            EmptyState(
                '🛡️',
                __('All Quiet', 'comments-press-zone'),
                __('Moderation actions will be logged here automatically.', 'comments-press-zone'),
                null,
                'welcome'
            )
        ]);
    }

    const icons = {
        approved: '✅',
        deleted: '🗑️',
        banned: '⛔',
        unbanned: '🔓',
        edited: '✏️',
        spam: '🚫'
    };

    const items = activities.map(activity => {
        const icon = icons[activity.action] || '📝';
        const timeAgo = formatTimeAgo(activity.timestamp);

        return el('div', { class: 'presszone-comments-timeline__item' },
            el('span', { class: 'presszone-comments-timeline__icon' }, icon),
            el('div', { class: 'presszone-comments-timeline__content' },
                el('span', {},
                    el('strong', {}, activity.moderator),
                    ` ${activity.action} comment by `,
                    el('em', {}, activity.comment_author)
                ),
                el('span', { class: 'presszone-comments-timeline__time' }, timeAgo)
            )
        );
    });

    const timeline = el('div', { class: 'presszone-comments-timeline' }, ...items);

    return Card('📋', __('Recent Moderation Activity', 'comments-press-zone'),
        __('Actions taken by moderators', 'comments-press-zone'), [timeline]);
}

/**
 * Format timestamp as time ago
 */
function formatTimeAgo(timestamp) {
    const date = new Date(timestamp);
    const now = new Date();
    const seconds = Math.floor((now - date) / 1000);

    if (seconds < 60) return __('Just now', 'comments-press-zone');
    if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
    if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`;
    if (seconds < 604800) return `${Math.floor(seconds / 86400)}d ago`;
    return date.toLocaleDateString();
}

export default { renderDashboard };
